import { useAuth } from '@/hooks/useAuth';
import { AccessDenied } from '@/pages/AccessDenied';
import NotFound from '@/pages/not-found';

/**
 * Super-admin-only route guard.
 *
 * The repo prefers permission gates over role equality (ADR 011 D3), and this
 * is the exception the rule allows for: no resource/action pair names "super
 * admin and nobody else". `organization:update` admits the property manager,
 * and every finer permission the SA holds, the PM holds too or can be granted.
 *
 * Mirrors the server's `requireSuperAdmin` / `requireRoleOrNotFound`
 * (server/middleware.ts) so a route and its endpoint cannot drift into
 * disagreeing about who may open it.
 *
 * SA-18 (868jay9qq) EX-06 — the default `mode="accessDenied"` renders
 * `<AccessDenied />` for non-SAs. SA-only lookup management (commit 4)
 * passes `mode="notFound"` so a non-SA renders `<NotFound />` instead:
 * the same JSON shape the server emits, the same UX the SPA already
 * uses for genuinely missing routes. Hiding the surface behind a 404
 * (instead of a 403) means a non-SA cannot tell that the catalogue
 * exists at all — there is no information leak about the SA-only
 * infrastructure.
 */
export type RequireSuperAdminMode = 'accessDenied' | 'notFound';

export function RequireSuperAdmin({
  children,
  mode = 'accessDenied',
}: {
  children: React.ReactNode;
  mode?: RequireSuperAdminMode;
}) {
  const { role, isLoading } = useAuth();
  if (isLoading) {
    return null;
  }
  if (role === 'super_admin') {
    return <>{children}</>;
  }
  if (mode === 'notFound') {
    return <NotFound />;
  }
  return <AccessDenied />;
}
